import React, { useState, useMemo } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { 
  Layers, 
  Layers3, 
  Plus, 
  Trash2, 
  Sparkles, 
  Grid, 
  Scale, 
  Eye, 
  ArrowRightLeft,
  Wand2,
  AlertTriangle
} from 'lucide-react';
import { Unit } from '../../types';
import { formatUnit, parseUnit } from '../../utils/units';
import { 
  GangingJob, 
  calculateGangingBalance, 
  DEFAULT_GANGING_JOBS 
} from '../../utils/gangingBalancer';

interface GangingAndNestingPanelProps {
  onClose?: () => void;
}

export const GangingAndNestingPanel: React.FC<GangingAndNestingPanelProps> = ({ onClose }) => {
  const { layout, setLayout, appUnit } = useSaaSContext();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'ganging' | 'balancer' | 'work_and_turn'>('ganging');

  // Local state for editing ganging jobs
  const [jobs, setJobs] = useState<GangingJob[]>(
    layout.gangingJobs && layout.gangingJobs.length > 0
      ? layout.gangingJobs
      : DEFAULT_GANGING_JOBS
  );

  const enableGanging = Boolean(layout.enableGanging);
  const rows = Number(layout.rows) || 1;
  const cols = Number(layout.cols) || 1;
  const totalSlots = rows * cols;

  // Compute Balancer Calculations
  const balanceResult = useMemo(() => {
    return calculateGangingBalance(jobs, totalSlots);
  }, [jobs, totalSlots]);

  const handleToggleGanging = (enabled: boolean) => {
    setLayout(prev => ({
      ...prev,
      enableGanging: enabled,
      gangingJobs: jobs
    }));
    toast.success(
      enabled ? 'Multi-Job Ganging Enabled' : 'Single-Design Mode Restored',
      enabled 
        ? `Active ganging sheet with ${jobs.length} mixed client jobs.` 
        : 'Reverted layout to single template repeat.'
    );
  };

  const handleAddJob = () => {
    if (jobs.length >= 10) {
      toast.error('Maximum Jobs Reached', 'You can gang up to 10 mixed client jobs per sheet.');
      return;
    }

    const newJob: GangingJob = {
      id: `job-${Date.now()}`,
      clientName: `Client ${String.fromCharCode(65 + jobs.length)}`,
      jobName: `Sticker Label 50x50mm`,
      width: 141.7, // 50mm
      height: 141.7,
      requiredQty: 250,
      color: ['#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'][jobs.length % 5]
    };

    const updated = [...jobs, newJob];
    setJobs(updated);
    setLayout(prev => ({ ...prev, gangingJobs: updated }));
  };

  const handleRemoveJob = (id: string) => {
    if (jobs.length <= 1) {
      toast.error('Cannot Remove', 'A ganging sheet requires at least 1 job.');
      return;
    }
    const updated = jobs.filter(j => j.id !== id);
    setJobs(updated);
    setLayout(prev => ({ ...prev, gangingJobs: updated }));
  };

  const handleUpdateJob = (id: string, field: keyof GangingJob, val: any) => {
    const updated = jobs.map(j => (j.id === id ? { ...j, [field]: val } : j));
    setJobs(updated);
    setLayout(prev => ({ ...prev, gangingJobs: updated }));
  };

  const handleLoadSampleBatch = () => {
    setJobs(DEFAULT_GANGING_JOBS);
    setLayout(prev => ({
      ...prev,
      enableGanging: true,
      gangingJobs: DEFAULT_GANGING_JOBS
    }));
    toast.success('Loaded Sample Batch', 'Added 3 mixed client jobs to the ganging sheet.');
  };

  const handleApplyBalancer = () => {
    if (balanceResult.isValid === false) {
      toast.error('Ganging Overflow', balanceResult.errorMessage || 'Cannot gang more jobs than available grid slots.');
      return;
    }

    setLayout(prev => ({
      ...prev,
      enableGanging: true,
      gangingJobs: balanceResult.jobs,
      runQuantityOverride: balanceResult.sheetsNeeded
    }));

    toast.success(
      'Sheet Balancer Applied',
      `Allocated ${balanceResult.totalSlots} slots across ${jobs.length} jobs. Print run: ${balanceResult.sheetsNeeded} sheets with ${balanceResult.efficiencyPercent}% efficiency.`,
      { duration: 4000 }
    );
  };

  // Work & Turn Settings
  const impositionType = layout.impositionType || 'single_sided';
  const viewSide = layout.viewSide || 'front';
  const backSideMode = layout.backSideMode || 'mirrored';

  return (
    <div className="flex flex-col gap-4 text-zinc-200">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-950/80 via-zinc-900 to-indigo-950/80 p-3.5 rounded-xl border border-purple-500/30 flex items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center shrink-0">
            <Layers3 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-white flex items-center gap-1.5">
              Multi-Design Ganging &amp; Work-and-Turn Studio
              <span className="text-[10px] bg-purple-500/20 text-purple-300 font-mono px-2 py-0.5 rounded-full border border-purple-500/30">
                Phase 3
              </span>
            </h3>
            <p className="text-xs text-zinc-400">
              Mix 2-10 client orders on 1 sheet, balance print ratios &amp; align 2-sided Work &amp; Turn graphics.
            </p>
          </div>
        </div>

        {/* Master Toggle */}
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-950/80 px-3 py-1.5 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-all">
          <span className="text-xs font-bold text-zinc-300">Ganging Sheet</span>
          <input 
            type="checkbox"
            checked={enableGanging}
            onChange={(e) => handleToggleGanging(e.target.checked)}
            className="w-4 h-4 accent-purple-500 cursor-pointer rounded"
          />
        </label>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1 p-1 bg-zinc-950 rounded-lg border border-zinc-800">
        <button
          type="button"
          onClick={() => setActiveTab('ganging')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'ganging'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Mixed Job Ganging ({jobs.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('balancer')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'balancer'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <Scale className="w-4 h-4" />
          <span>Sheet Balancer</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('work_and_turn')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'work_and_turn'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <ArrowRightLeft className="w-4 h-4" />
          <span>Work &amp; Turn (2-Sided)</span>
        </button>
      </div>

      {/* TAB 1: MULTI-JOB GANGING SHEET MANAGER */}
      {activeTab === 'ganging' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                <Grid className="w-4 h-4 text-purple-400" />
                1. Multi-Client Order Gang Manager (2 to 10 Jobs)
              </h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">Combine different sticker sizes and designs onto a single imposition sheet.</p>
            </div>

            <button
              type="button"
              onClick={handleLoadSampleBatch}
              className="text-[11px] bg-purple-950 border border-purple-800 text-purple-300 hover:bg-purple-900 font-semibold px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Load Sample 3-Client Batch
            </button>
          </div>

          {/* Ganging Overflow Warning Banner */}
          {balanceResult.isValid === false && (
            <div className="bg-red-950/90 border border-red-500/80 rounded-xl p-3 flex items-start gap-2.5 text-red-200 text-xs shadow-sm">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold text-red-100 block">Ganging Slot Overflow ({jobs.length} Jobs vs {totalSlots} Slots)</strong>
                <p className="text-[11px] text-red-300 mt-0.5">
                  You have configured {jobs.length} jobs, but the current grid ({rows}×{cols}) only has {totalSlots} slots. 
                  Increase the sheet rows/columns or remove {balanceResult.overflowCount} job{balanceResult.overflowCount === 1 ? '' : 's'} so every job receives at least 1 slot.
                </p>
              </div>
            </div>
          )}

          {/* Job Cards List */}
          <div className="space-y-3">
            {jobs.map((job, idx) => {
              const widthDisplay = formatUnit(job.width, appUnit as Unit);
              const heightDisplay = formatUnit(job.height, appUnit as Unit);

              return (
                <div key={job.id} className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 space-y-2 hover:border-zinc-700 transition-all">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-3 h-3 rounded-full shrink-0 border border-white/20 shadow-xs" 
                        style={{ backgroundColor: job.color }}
                      />
                      <span className="text-xs font-bold text-white font-mono">Job #{idx + 1}</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveJob(job.id)}
                      className="text-zinc-500 hover:text-red-400 p-1 rounded hover:bg-zinc-800 transition-colors cursor-pointer"
                      title="Remove Job"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs">
                    
                    {/* Client Name */}
                    <div>
                      <label className="text-[10px] text-zinc-400 font-semibold">Client Name</label>
                      <input 
                        type="text"
                        value={job.clientName}
                        onChange={(e) => handleUpdateJob(job.id, 'clientName', e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded outline-none focus:border-purple-500 font-semibold"
                      />
                    </div>

                    {/* Job / Design Label */}
                    <div>
                      <label className="text-[10px] text-zinc-400 font-semibold">Design Label</label>
                      <input 
                        type="text"
                        value={job.jobName}
                        onChange={(e) => handleUpdateJob(job.id, 'jobName', e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded outline-none focus:border-purple-500"
                      />
                    </div>

                    {/* Dimensions */}
                    <div>
                      <label className="text-[10px] text-zinc-400 font-semibold">Dimensions ({appUnit})</label>
                      <div className="flex items-center gap-1">
                        <input 
                          type="number"
                          value={widthDisplay}
                          onChange={(e) => handleUpdateJob(job.id, 'width', parseUnit(e.target.value, appUnit as Unit))}
                          className="w-1/2 bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2 py-1.5 rounded outline-none focus:border-purple-500 font-mono text-center"
                          placeholder="W"
                        />
                        <span className="text-zinc-500 font-bold">×</span>
                        <input 
                          type="number"
                          value={heightDisplay}
                          onChange={(e) => handleUpdateJob(job.id, 'height', parseUnit(e.target.value, appUnit as Unit))}
                          className="w-1/2 bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2 py-1.5 rounded outline-none focus:border-purple-500 font-mono text-center"
                          placeholder="H"
                        />
                      </div>
                    </div>

                    {/* Required Quantity */}
                    <div>
                      <label className="text-[10px] text-zinc-400 font-semibold">Required Qty</label>
                      <input 
                        type="number"
                        min="1"
                        value={job.requiredQty}
                        onChange={(e) => handleUpdateJob(job.id, 'requiredQty', Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1.5 rounded outline-none focus:border-purple-500 font-mono font-bold text-purple-400"
                      />
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Job Button */}
          <button
            type="button"
            onClick={handleAddJob}
            className="w-full py-2 bg-zinc-900 hover:bg-zinc-800 border border-dashed border-zinc-700 hover:border-purple-500 text-zinc-300 font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4 text-purple-400" />
            Add Another Client Order ({jobs.length}/10 Jobs)
          </button>

        </div>
      )}

      {/* TAB 2: QUANTITY-BASED SHEET BALANCER */}
      {activeTab === 'balancer' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                <Scale className="w-4 h-4 text-indigo-400" />
                2. Quantity-Based Sheet Balancer &amp; Waste Minimizer
              </h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">Auto-calculates exact grid slot ratios per job to hit order targets simultaneously with zero wasted sheets.</p>
            </div>

            <span className="text-[10px] bg-zinc-900 border border-zinc-800 text-indigo-400 px-2.5 py-1 rounded font-mono font-bold">
              Grid: {rows}×{cols} ({totalSlots} Slots)
            </span>
          </div>

          {/* Ganging Overflow Warning Banner */}
          {balanceResult.isValid === false && (
            <div className="bg-red-950/90 border border-red-500/80 rounded-xl p-3 flex items-start gap-2.5 text-red-200 text-xs shadow-sm">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold text-red-100 block">Cannot Auto-Balance: {jobs.length} Jobs vs {totalSlots} Available Slots</strong>
                <p className="text-[11px] text-red-300 mt-0.5">
                  Each ganging job requires at least 1 dedicated grid slot. Please adjust the grid size in layout settings or reduce the number of jobs.
                </p>
              </div>
            </div>
          )}

          {/* Balancer Metrics Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
            <div className="bg-zinc-900 p-3 rounded-xl border border-zinc-800 space-y-1">
              <span className="text-[10px] text-zinc-400 uppercase font-sans">Total Required</span>
              <div className="text-base font-bold font-mono text-white">{balanceResult.totalRequired.toLocaleString()}</div>
            </div>

            <div className="bg-zinc-900 p-3 rounded-xl border border-zinc-800 space-y-1">
              <span className="text-[10px] text-zinc-400 uppercase font-sans">Total Produced</span>
              <div className="text-base font-bold font-mono text-purple-400">{balanceResult.totalProduced.toLocaleString()}</div>
            </div>

            <div className="bg-zinc-900 p-3 rounded-xl border border-zinc-800 space-y-1">
              <span className="text-[10px] text-zinc-400 uppercase font-sans">Sheets Needed</span>
              <div className="text-base font-bold font-mono text-emerald-400">{balanceResult.sheetsNeeded} Sheets</div>
            </div>

            <div className="bg-zinc-900 p-3 rounded-xl border border-zinc-800 space-y-1">
              <span className="text-[10px] text-zinc-400 uppercase font-sans">Media Efficiency</span>
              <div className="text-base font-bold font-mono text-indigo-400">{balanceResult.efficiencyPercent}%</div>
            </div>
          </div>

          {/* Job Slot Distribution Table */}
          <div className="bg-zinc-900/80 p-3.5 rounded-xl border border-zinc-800 space-y-2.5">
            <h5 className="text-xs font-bold text-zinc-200">Calculated Slot Ratios per Sheet</h5>

            <div className="space-y-2">
              {balanceResult.jobs.map((j) => {
                const slots = j.assignedSlots || 0;
                const percent = totalSlots > 0 ? Math.round((slots / totalSlots) * 100) : 0;
                const produced = balanceResult.sheetsNeeded * slots;
                const overrun = Math.max(0, produced - j.requiredQty);

                return (
                  <div key={j.id} className="bg-zinc-950 p-2.5 rounded-lg border border-zinc-800 flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: j.color }} />
                      <div className="truncate">
                        <div className="font-bold text-white truncate">{j.clientName}</div>
                        <div className="text-[10px] text-zinc-400 truncate">{j.jobName}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 font-mono text-right shrink-0">
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block">Req</span>
                        <span className="text-zinc-300">{j.requiredQty}</span>
                      </div>

                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block">Slots</span>
                        <span className="text-purple-400 font-bold">{slots}/{totalSlots} ({percent}%)</span>
                      </div>

                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase block">Yield</span>
                        <span className="text-emerald-400 font-bold">{produced}</span>
                      </div>

                      {overrun > 0 && (
                        <div>
                          <span className="text-[10px] text-zinc-500 uppercase block">Overrun</span>
                          <span className="text-amber-400">+{overrun}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Apply Balancer Action Button */}
          <button
            type="button"
            disabled={balanceResult.isValid === false}
            onClick={handleApplyBalancer}
            className={`w-full py-2.5 font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 ${
              balanceResult.isValid === false
                ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed border border-zinc-700'
                : 'bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer'
            }`}
          >
            <Wand2 className="w-4 h-4" />
            {balanceResult.isValid === false
              ? `Cannot Apply (${jobs.length} Jobs Exceed ${totalSlots} Slots)`
              : `Apply Auto-Balanced Ratios to Sheet (${balanceResult.sheetsNeeded} Sheets Total)`}
          </button>

        </div>
      )}

      {/* TAB 3: WORK & TURN / FRONT-BACK IMPOSITION */}
      {activeTab === 'work_and_turn' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-purple-400" />
                3. Work &amp; Turn / Front-Back Imposition (2-Sided Registration)
              </h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">Back-to-back registration alignment for double-sided window graphics, decals &amp; instructions.</p>
            </div>
          </div>

          {/* Imposition Mode Selector */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-zinc-300">Select 2-Sided Imposition Mode</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: 'single_sided', title: 'Single-Sided', desc: 'Standard 1-sided printing' },
                { id: 'work_and_turn', title: 'Work & Turn', desc: 'Horizontal flip (Left-Right axis)' },
                { id: 'work_and_tumble', title: 'Work & Tumble', desc: 'Vertical flip (Top-Bottom axis)' },
                { id: 'perfecting', title: 'Perfecting Pass', desc: 'Dual-pass back-to-back sheet' }
              ].map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setLayout(prev => ({ ...prev, impositionType: mode.id as any }))}
                  className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                    impositionType === mode.id
                      ? 'bg-purple-950/60 border-purple-500 text-white shadow-xs'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  <span className="text-xs font-bold text-zinc-200">{mode.title}</span>
                  <span className="text-[10px] text-zinc-500 mt-1 leading-tight">{mode.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* View Side & Registration Toggle */}
          {impositionType !== 'single_sided' && (
            <div className="bg-zinc-900/80 p-3.5 rounded-xl border border-zinc-800 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-zinc-300 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-purple-400" />
                  Imposition Canvas View Side
                </span>

                <div className="flex items-center gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800">
                  <button
                    type="button"
                    onClick={() => setLayout(prev => ({ ...prev, viewSide: 'front' }))}
                    className={`py-1 px-3 rounded text-xs font-bold transition-all cursor-pointer ${
                      viewSide === 'front'
                        ? 'bg-purple-600 text-white'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    Front Side
                  </button>

                  <button
                    type="button"
                    onClick={() => setLayout(prev => ({ ...prev, viewSide: 'back' }))}
                    className={`py-1 px-3 rounded text-xs font-bold transition-all cursor-pointer ${
                      viewSide === 'back'
                        ? 'bg-purple-600 text-white'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    Back Side (Mirrored)
                  </button>
                </div>
              </div>

              {/* Back Side Mode */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-zinc-300">Back Side Content Mode</label>
                <select
                  value={backSideMode}
                  onChange={(e) => setLayout(prev => ({ ...prev, backSideMode: e.target.value as any }))}
                  className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-purple-500 font-mono"
                >
                  <option value="mirrored">Mirrored Front Design (For double-sided window stickers)</option>
                  <option value="blank">Blank White Backing (Unprinted adhesive substrate)</option>
                  <option value="custom_text">Custom Rear Instructions / Legal Disclaimer</option>
                </select>
              </div>

              {backSideMode === 'custom_text' && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-zinc-300">Rear Side Disclaimer / Instruction Text</label>
                  <textarea
                    value={layout.backSideText || 'REAR INSTRUCTIONS: Peel carefully before applying to glass. ISO 9001 Certified.'}
                    onChange={(e) => setLayout(prev => ({ ...prev, backSideText: e.target.value }))}
                    rows={2}
                    className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs p-2.5 rounded-lg outline-none focus:border-purple-500 font-mono"
                  />
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* Footer Action Buttons */}
      <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[10px] text-zinc-500 font-mono">Ganging & Duplex Engine Active</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Apply & Close
          </button>
        )}
      </div>
    </div>
  );
};
